import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import { CITIES } from "@/cityConfig";

// Direct city-to-city cross-linking: links to sibling city CATALOGS in the same country.
// Mirrors the bot prerender "nearby cities" block so users and search engines see the same links.
// Regional priority neighbors (e.g. Liguria: Genoa <-> Portofino) surface first so they are
// never cut off by the display limit when a country has many active cities.
const PRIORITY_NEIGHBORS = {
  genoa: ["portofino"],
  portofino: ["genoa"],
  "porto-rotondo": ["porto-cervo"],
  "porto-cervo": ["porto-rotondo"],
  santorini: ["mykonos"],
  mykonos: ["santorini"],
};

// Cities that already have a dedicated "nearby" block covering all their siblings,
// so the generic list would just duplicate the same links.
const HAS_DEDICATED_NEARBY = new Set(["bangkok", "phuket", "pattaya", "colombo"]);

export const NearbyCitiesLinks = ({ citySlug, lang = "ru" }) => {
  const current = CITIES[citySlug];
  if (!current) return null;
  if (HAS_DEDICATED_NEARBY.has(citySlug)) return null;

  const priority = PRIORITY_NEIGHBORS[citySlug] || [];
  const siblings = Object.values(CITIES)
    .filter((c) => c.slug !== citySlug && c.country === current.country)
    .sort((a, b) => {
      const ai = priority.indexOf(a.slug);
      const bi = priority.indexOf(b.slug);
      if (ai !== -1 || bi !== -1) {
        return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
      }
      return 0;
    })
    .slice(0, 6);

  if (siblings.length === 0) return null;

  const langSuffix = lang !== "ru" ? `?lang=${lang}` : "";
  const countryName =
    lang === "en" ? current.countryNameEn || current.countryName : current.countryName;

  const heading =
    lang === "en"
      ? `Flower delivery in other cities of ${countryName}`
      : lang === "tr"
      ? `${countryName} genelinde diğer şehirlere çiçek teslimatı`
      : `Доставка цветов в другие города — ${countryName}`;

  const anchor = (c) => {
    if (lang === "en") return `Flower delivery ${c.namePrepositionalEn || `in ${c.nameEn || c.name}`}`;
    return `Доставка цветов ${c.namePrepositional || `в ${c.name}`}`;
  };

  return (
    <section className="py-12 sm:py-16 bg-white" data-testid="nearby-cities-links">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <h2
          className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8"
          style={{ fontFamily: "Playfair Display, serif" }}
        >
          <MapPin className="w-6 h-6 text-pink-500 inline-block mr-2 -mt-1" />
          {heading}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {siblings.map((c) => (
            <Link
              key={c.slug}
              to={`/${c.slug}${langSuffix}`}
              className="group flex items-center justify-between p-4 sm:p-5 rounded-2xl border border-gray-100 hover:border-pink-200 hover:shadow-lg transition-all bg-gray-50 hover:bg-white"
              data-testid={`nearby-city-${c.slug}`}
            >
              <span className="text-sm sm:text-base font-semibold text-gray-900 group-hover:text-pink-600 transition-colors">
                {anchor(c)}
              </span>
              <ArrowRight className="w-4 h-4 text-pink-500 group-hover:translate-x-1 transition-transform shrink-0" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default NearbyCitiesLinks;
